import React, { useCallback, useEffect, useState } from 'react'
import { Select } from '../Employee/Select/Select'
import { useIntl } from 'react-intl'
import { FaSearch } from 'react-icons/fa'
import { successionPlanTranslate } from '../../i18n/translatedMessages'
import { TableSuccessionPlan } from './tableSuccessionPlan'
import { Spinner } from 'react-bootstrap'

import { translate as translateAreas } from '../../i18n/Library/areasCorrelation.js'
import { translate as translatePosition } from '../../i18n/Library/hierarchicalLevel'
import { translate as translateSubareas } from '../../i18n/Library/subareasTranslate.js'

import successionPlan from './../../API/SuccessionPlan/successionPlan'
import List from './../../API/Lists/Lists.js'
import countryMapper from '../../Utils/CountriesMapper.js'
import areasMapper from '../../Utils/AreasMapper.js'
import levelsMapper from '../../Utils/LevelsMapper.js'
import subareasMapper from '../../Utils/SubareasMapper.js'
import PDFButtonSuccessionPlan from '../PDFSuccessionPlan/pdfSuccessionPlan'
import ModalSavedPlans from './ModalSavedPlans/modalSavedPlans'
import { NextStepModal } from '../Career/nextStep'
import CareerApi from '../../API/career/Career.api'

export const SuccessionPlan = () => {
	const getLanguage = useIntl()
	const language = getLanguage.locale === 'PT-BR' ? 'pt' : 'es'

	const [showModal, setShowModal] = useState(false)
	const [stepModal, setStepModal] = useState(false)
	const [savedPlans, setSavedPlans] = useState([])

	const [employees, setEmployees] = useState([])
	const [arrayBufferPDF, setArrayBufferPDF] = useState('')

	const [loading, setLoading] = useState(false)
	const [retrivingData, setRetrivingData] = useState(false)
	const [generatePDF, setGeneratePDF] = useState(null)

	const [planName, setPlanName] = useState('')
	const [selectedCountry, setSelectedCountry] = useState('')

	const [filters, setFilters] = useState({
		country: '',
		area: '',
		level: '',
		subarea: '',
	})

	const [lists, setLists] = useState({
		areas: [],
		levels: [],
		subareas: [],
		countries: [],
	})

	const [selectedData, setSelectedData] = useState({
		id: '',
		status: '',
		level_id: '',
		area_id: '',
		subarea_id: '',
	})

	//filter controllers
	const handleFilter = e => {
		const selectedOption = e.target.value
		const fieldName = e.target.name

		setFilters(prev => ({
			...prev,
			[fieldName]: selectedOption || null,
			...(fieldName === 'area' && { subarea: null }),
		}))
	}

	const translatedAreas = lists.areas.map(item => ({
		...item,
		value: translateAreas[language][item?.value],
	}))

	const translatedLevels = lists.levels.map(item => ({
		...item,
		value: translatePosition[language][item?.value],
	}))

	const filteredAndTranslatedSubareas = filters.area
		? lists.subareas
				.filter(subarea => subarea.area === filters.area)
				.map(item => ({
					...item,
					value: translateSubareas[language][item?.value] || item?.value,
				}))
		: []

	//getDatas
	const fetchData = useCallback(async () => {
		try {
			setLoading(true)

			const [resArea, resCountry, resLevel, resSubareas] = await Promise.all([List.areas(), List.countries(), List.levels(), List.subareas()])

			const areas = resArea.map(areasMapper.toDomain)
			const subareas = resSubareas.map(subareasMapper.toDomain)
			const countries = resCountry.map(countryMapper.toDomain).filter(country => country.value !== 'Uruguay')
			const levels = resLevel.map(levelsMapper.toDomain)
			countries.unshift({ value: 'Indiferente', id: '0' })

			setLists({ areas, levels, subareas, countries })
		} catch (error) {
			console.error('Error:', error)
		} finally {
			setLoading(false)
		}
	}, [])

	const fetchEmployees = useCallback(async () => {
		try {
			setLoading(true)
			const companyId = JSON.parse(localStorage.getItem('user')).id
			const { area, level, country, subarea } = filters

			const [resList] = await Promise.all([successionPlan.getAllEmployeesSuccessionPlan(companyId, level, area, subarea)])

			const filteredEmployees = country != 0 ? resList.filter(employee => employee.country_id === country) : resList

			setEmployees(filteredEmployees.sort((a, b) => b.algorithm_score - a.algorithm_score))
		} finally {
			setLoading(false)
		}
	}, [filters])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const handleSearch = () => {
		fetchEmployees()
	}

	const openModal = async () => {
		try {
			setRetrivingData(true)
			const companyId = JSON.parse(localStorage.getItem('user')).id
			const response = await successionPlan.getSavePlans(companyId)
			const data = response
			setSavedPlans(data)
			setShowModal(true)
			setRetrivingData(false)
		} catch (error) {
			console.error('Error to get plans:', error)
			setRetrivingData(false)
		}
	}

	const deletePlan = async id => {
		try {
			console.log('id do plano delete: ', id, ' planos: ', savedPlans)
			await successionPlan.deletePlan(id)
			setSavedPlans(savedPlans.filter(plan => plan.id !== id))
		} catch (error) {
			console.error('Error while deleting plan: ', error)
		}
	}

	const savePlan = async () => {
		try {
			const nextLevel = lists.levels.find(level => String(level.id) === String(filters.level))
			const nextArea = lists.areas.find(area => String(area.id) === String(filters.area))
			const nextSubarea = lists.subareas.find(subarea => String(subarea.id) === String(filters.subarea))
			const country = lists.countries.find(country => String(country.id) === String(filters.country))

			setPlanName(`${nextLevel?.value ?? '-'} de ${nextSubarea?.value ?? nextArea?.value}`)
			setSelectedCountry(country?.value ?? '-')
			setGeneratePDF(true)
		} catch (error) {
			console.log('Error while saving plan:', error)
		}
	}

	const closeModal = () => setShowModal(false)

	const closeStepModal = async () => {
		setStepModal(false)
	}

	const sendData = async data => {
		try {
			const newStep = {
				'employee_id': selectedData.employee_id,
				'level_id': data.level_id,
				'area_id': data.area_id,
				'subarea_id': data.subarea,
				'chosen_by': 'hr',
			}

			await CareerApi.addNewCareerEmployee(newStep)

			setEmployees(prevEmployees =>
				prevEmployees.map(employee =>
					employee.employee_id === selectedData.employee_id
						? {
								...employee,
								next_steps_hr: [...employee.next_steps_hr, newStep],
						  }
						: employee
				)
			)

			setStepModal(false)
		} catch (error) {}
	}

	const handleAddNextStepClick = employee => {
		setSelectedData(employee)
		setStepModal(true)
	}

	return (
		<>
			<PDFButtonSuccessionPlan
				employeeList={employees}
				planName={planName}
				countryName={selectedCountry}
				filters={filters}
				shouldGeneratePDF={generatePDF}
				lists={lists}
				onPDFGenerated={() => setGeneratePDF(false)}
				language={language}
			/>

			{stepModal && <NextStepModal onClose={closeStepModal} onSave={sendData} data={selectedData} lists={lists} language={language} />}

			{showModal && <ModalSavedPlans savedPlans={savedPlans} onClose={closeModal} onDelete={deletePlan} lists={lists} />}

			<div className='tw-mb-6 tw-flex tw-justify-between tw-items-center tw-pr-20'>
				<div>
					<h4 className='tw-font-bold'>{successionPlanTranslate.successionPlanTitle}</h4>
					<div>{successionPlanTranslate.successionPlanDesc}</div>
				</div>
				<div className='tw-flex tw-space-x-2'>
					{/* <button className='tw-bg-white border tw-min-w-[160px] tw-max-w-[190px] tw-text-black tw-px-4 tw-py-1 tw-rounded-lg' onClick={openModal}>
						{retrivingData ? <Spinner animation='border' variant='primary' /> : successionPlanTranslate.successionPlanShowPlans}
					</button> */}
					<button
						className={`${
							employees >= 0 ? 'tw-bg-gray-400' : 'tw-bg-gray-900 tw-cursor-pointer'
						} tw-min-w-[80px] tw-max-w-[108px]  tw-border-none tw-text-white tw-px-4 tw-py-1 tw-rounded-lg`}
						onClick={savePlan}
						disabled={employees >= 0}
					>
						{generatePDF ? <Spinner animation='border' variant='primary' /> : 'Exportar Plan'}
					</button>
				</div>
			</div>

			<div className='tw-w-full tw-flex tw-space-x-3 tw-items-center tw-pr-20'>
				<div className='tw-w-1/5'>
					<Select placeholder={'Nivel Hierárquico'} list={translatedLevels} onChange={handleFilter} name='level' value={filters.level} />
				</div>

				<div className='tw-w-1/5'>
					<Select placeholder='Área' list={translatedAreas} onChange={handleFilter} name='area' value={filters.area} />
				</div>

				<div className='tw-w-1/5'>
					<Select placeholder='Subarea' list={filteredAndTranslatedSubareas} onChange={handleFilter} name='subarea' value={filters.subarea} />
				</div>

				<div className='tw-w-1/5'>
					<Select placeholder='País' list={lists.countries} onChange={handleFilter} name='country' value={filters.country} />
				</div>

				<button
					className={`tw-w-1/5 tw-text-sm tw-flex tw-px-2 tw-items-center tw-justify-center  ${
						!filters.area || !filters.level ? 'tw-text-gray-400' : ' tw-bg-purple-500 tw-text-white'
					}`}
					onClick={handleSearch}
					disabled={!filters.area || !filters.level}
					style={{ height: 45, border: '1px solid #CBD5E1', borderRadius: 8 }}
				>
					<FaSearch className='me-1' size={12} />
					{successionPlanTranslate.successionPlanGeneratePlan}
				</button>
			</div>

			<TableSuccessionPlan
				filters={filters}
				employees={employees}
				loading={loading}
				lists={lists}
				setEmployees={setEmployees}
				handleAddNextStepClick={handleAddNextStepClick}
				language={language}
			/>
		</>
	)
}
